import React from 'react';
import { Order, Id } from '@hero/schema';
import { GoogleMapWithMarker, MapLocationData, SavePreparingReason } from '@hero/web-components';
import { useOrderMap, useOrderDrawerStore } from '@hero/hooks-shared';
import { isOrderFieldEditable } from '@hero/core';
import { OrderInfoCardWrapper } from './OrderInfoCardWrapper';

export interface OrderMapProps {
  order: Order;
  mode?: 'view' | 'edit' | 'auto';
  /** When true, the map is purely view-only regardless of order status. No edit controls, no save location modal. */
  readOnly?: boolean;
  /** Callback when a location changes (real-time updates during editing) */
  onLocationChange?: (location: MapLocationData, index: number) => void;
  /** Callback when locations are finalized/ready (called when editing completes) */
  onLocationsReady?: (locations: MapLocationData[]) => void;
  /** Callback when the pins settle (edit done) but the user hasn't saved yet — use for pre-save work like quoting the new lane. */
  onLocationsFinalized?: (locations: MapLocationData[]) => void;
  /** Callback when changes are cancelled */
  onCancel?: () => void;
  onModeChange?: (mode: 'view' | 'edit') => void;
  /** When true, Save button shows loading state (same as form modals). From update order mutation isPending. */
  saveInProgress?: boolean;
  /** When true, Save is disabled while pre-save work is in flight. */
  savePreparing?: boolean;
  /** What that pre-save work is, so Save can name it. */
  savePreparingReason?: SavePreparingReason;
  className?: string;
  apiKey?: string;
  orgId?: string;
  customerId?: Id;
}

/**
 * OrderMap - Shared component for displaying order destinations on a map
 * Supports view/edit modes with location saving capabilities
 */
export const OrderMap: React.FC<OrderMapProps> = ({
  order,
  mode = 'view',
  readOnly = false,
  onLocationChange,
  onLocationsReady,
  onLocationsFinalized,
  onCancel,
  onModeChange,
  saveInProgress = false,
  savePreparing = false,
  savePreparingReason,
  className = '',
  apiKey,
  orgId,
  customerId,
}) => {
  const canEditDestination = isOrderFieldEditable('destination', order.status?.order);
  const isReadOnly = readOnly || !canEditDestination;
  const effectiveMode = isReadOnly ? 'view' : mode;

  const { mapLocations, isEditMode, getMarkerConfig } = useOrderMap({
    order,
    mode: effectiveMode,
  });

  const openOrderDrawer = useOrderDrawerStore(s => s.open);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <GoogleMapWithMarker
        className="w-full h-full"
        apiKey={apiKey}
        searchMode={isEditMode ? 'google-and-saved-locations' : 'no-search'}
        mapConfig={{
          height: '100%',
          zoom: 10,
          autoFitMarkerBounds: true,
          boundsPadding: 50,
          showUserCurrentLocation: true,
          routeLineType: 'straight',
          markerIcon: 'order_pickup_dropoff_icons',
        }}
        clientConfig={{
          orgId: orgId || order.shipper?._id,
          thirdPartyCustomerId: customerId,
        }}
        locationsConfig={{
          countLimit: undefined,
          enableAccurateLocationToggle: !isReadOnly,
          enableSavingPlaces: !isReadOnly,
          enableAccurateLocationIndicator: !isReadOnly,
          mode: isReadOnly ? 'view' : 'edit',
        }}
        locations={mapLocations}
        enableConfirmLocationsButton={true}
        saveInProgress={saveInProgress}
        savePreparing={savePreparing}
        savePreparingReason={savePreparingReason}
        onLocationChange={onLocationChange}
        onLocationsReady={onLocationsReady}
        onLocationsFinalized={onLocationsFinalized}
        onCancel={onCancel}
        onLocationEditStart={() => onModeChange?.('edit')}
        onLocationEditEnd={() => onModeChange?.('view')}
        getMarkerConfig={getMarkerConfig}
        renderBottomCard={() => (
          <OrderInfoCardWrapper order={order} onClick={() => openOrderDrawer(order._id.toString())} />
        )}
      />
    </div>
  );
};
